import { useCallback, useEffect, useState } from 'react';
import type { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

const hasMeaningfulValue = (value: unknown) => {
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined;
};

/**
 * Tracks whether all named form fields currently have meaningful values without
 * subscribing the component to every value change. This is intended for cheap UI
 * state such as optional-step completion ticks, not authoritative validation.
 */
export function useHasFormValues<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  form: Pick<UseFormReturn<TFieldValues>, 'getValues' | 'subscribe'>,
  names: readonly TName[],
  hasValue: (value: unknown) => boolean = hasMeaningfulValue,
) {
  const getHasValues = useCallback(
    () => names.every((name) => hasValue(form.getValues(name))),
    [form, names, hasValue],
  );
  const [hasValues, setHasValues] = useState(getHasValues);

  useEffect(() => {
    setHasValues(getHasValues());

    return form.subscribe({
      name: names,
      formState: { values: true },
      exact: true,
      callback: () => {
        setHasValues((current) => {
          const next = getHasValues();
          return current === next ? current : next;
        });
      },
    });
  }, [form, names, getHasValues]);

  return hasValues;
}
